import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { Button } from '@7f-ui/components/button/button';
import { Select } from '@7f-ui/components/form/select';
import { AmountPicker } from '@7f-ui/components/inputs_old/amount-picker';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import type { Accommodation, AccommodationImportantNotes } from '@data-access/graphql/schema.types';
import { createAllocation, getDefaultAllocation, type PaxParams, paxParser } from '@feat/feat-url-state/pax';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { TrashSimple } from '@phosphor-icons/react';
import { shopTestId } from '@utils/utils-shop-test-ids';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import * as v from 'valibot';
import { CategoryPriceLabel } from './category-price-label';
import { PaxContactInfos } from './pax-contact-infos';
export type PaxPickerAccommodation = Pick<Accommodation, 'occupancy' | 'servicePhone'> & {
  importantNotes: Pick<AccommodationImportantNotes, 'key'>[];
};
export interface PaxModalContentProps {
  onSubmit: (config: PaxParams) => void;
  onDelete?: () => void;
  initialValues: Partial<PaxParams>;
  primaryLabel?: string;
  accommodation?: PaxPickerAccommodation;
  showRoomsPicker?: boolean;
  showContactInfo?: boolean;
}
export function PaxModalContent(t0) {
  const $ = _c(83);
  const {
    onSubmit,
    onDelete,
    initialValues,
    primaryLabel,
    accommodation,
    showContactInfo: t1,
    showRoomsPicker: t2
  } = t0;
  const showContactInfo = t1 === undefined ? true : t1;
  const showRoomsPicker = t2 === undefined ? true : t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== initialValues.adults || $[1] !== initialValues.allocation?.length || $[2] !== initialValues.children || $[3] !== showRoomsPicker) {
    const numberFromSelect = v.union([v.number("Das Feld darf nicht leer sein. Bitte Text pflegen oder Feld l\xF6schen."), v.pipe(v.string(), v.minLength(1, "Das Feld darf nicht leer sein. Bitte Text pflegen oder Feld l\xF6schen."), v.check(_temp, "Bitte gib eine g\xFCltige Zahl ein"), v.transform(Number))]);
    let t6;
    if ($[7] !== showRoomsPicker) {
      t6 = data => !showRoomsPicker || !data.rooms || data.rooms <= data.adults;
      $[7] = showRoomsPicker;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    const validationSchema = v.pipe(v.object({
      adults: v.number(),
      children: v.array(v.object({
        value: numberFromSelect
      })),
      rooms: v.optional(v.number())
    }), v.forward(v.check(t6, "Pro Zimmer muss mindestens ein Erwachsener angegeben werden."), ["rooms"]));
    t5 = useForm;
    const t7 = initialValues.adults ?? paxParser.adults.defaultValue;
    const t8 = initialValues.children ?? paxParser.children.defaultValue;
    let t9;
    if ($[9] !== t8) {
      t9 = mapChildren(t8);
      $[9] = t8;
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    const t10 = showRoomsPicker ? initialValues.allocation?.length ?? 1 : undefined;
    if ($[11] !== t10 || $[12] !== t7 || $[13] !== t9) {
      t3 = {
        adults: t7,
        children: t9,
        rooms: t10
      };
      $[11] = t10;
      $[12] = t7;
      $[13] = t9;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t4 = valibotResolver(validationSchema);
    $[0] = initialValues.adults;
    $[1] = initialValues.allocation?.length;
    $[2] = initialValues.children;
    $[3] = showRoomsPicker;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4) {
    t6 = {
      defaultValues: t3,
      resolver: t4
    };
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const {
    control,
    formState,
    handleSubmit
  } = t5(t6);
  let t7;
  if ($[18] !== control) {
    t7 = {
      control,
      name: "children"
    };
    $[18] = control;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const {
    fields,
    replace,
    append
  } = useFieldArray(t7);
  let t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [{
      label: "Ausw\xE4hlen",
      value: ""
    }, ...Array.from({
      length: 18
    }).map(_temp2)];
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  t8 = t9;
  const ageOptions = t8;
  const adults = useWatch({
    control,
    name: "adults"
  }) ?? paxParser.adults.defaultValue;
  const children = useWatch({
    control,
    name: "children"
  }) ?? [];
  let t10;
  if ($[21] !== control) {
    t10 = {
      control,
      name: "rooms"
    };
    $[21] = control;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const rooms = useWatch(t10);
  const hasMultipleRooms = showRoomsPicker && rooms && rooms > 1;
  const maxOccupancyReached = accommodation && !hasMultipleRooms && adults + children.length >= (accommodation.occupancy?.maxOccupancy ?? 0);
  const maxChildOccupancyReached = accommodation && !hasMultipleRooms && children.length === (accommodation.occupancy?.maxChildren ?? 0);
  let t11;
  if ($[23] !== append) {
    t11 = () => {
      append({
        value: ""
      });
    };
    $[23] = append;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const addChild = t11;
  let t12;
  if ($[25] !== children || $[26] !== replace) {
    t12 = () => {
      replace(children.slice(0, children.length - 1));
    };
    $[25] = children;
    $[26] = replace;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const removeChild = t12;
  let t13;
  if ($[28] !== onSubmit) {
    t13 = data_0 => {
      const values = {
        ...data_0,
        children: data_0.children.map(_temp3)
      };
      const currentRooms = values.rooms;
      if (currentRooms === undefined || currentRooms === 1) {
        const allocation = getDefaultAllocation(values);
        return onSubmit({
          ...values,
          allocation
        });
      }
      const allocation_0 = determineAllocation(values.adults, values.children, currentRooms);
      onSubmit({
        ...values,
        allocation: allocation_0
      });
    };
    $[28] = onSubmit;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== handleSubmit || $[31] !== t13) {
    t14 = handleSubmit(t13);
    $[30] = handleSubmit;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleFormSubmit = t14;
  let t15;
  if ($[33] !== handleFormSubmit) {
    t15 = t16 => {
      const {
        children: content,
        ...props
      } = t16;
      return <form {...props} onSubmit={handleFormSubmit} data-testid={shopTestId("PaxPicker.root")} id="pax-selector-form">{content}</form>;
    };
    $[33] = handleFormSubmit;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== primaryLabel) {
    t16 = () => <Modal.Footer><Modal.Footer.Actions><Modal.Footer.Button type="submit" variant="primary">{primaryLabel || "\xDCbernehmen"}</Modal.Footer.Button></Modal.Footer.Actions></Modal.Footer>;
    $[35] = primaryLabel;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <CategoryPriceLabel title="Erwachsene" description="18 - 99 Jahre" />;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== maxOccupancyReached) {
    t18 = t19 => {
      const {
        field: t20
      } = t19;
      const {
        value: value_0,
        onChange
      } = t20;
      return <AmountPicker value={value_0 ?? paxParser.adults.defaultValue} decreaseDisabled={(value_0 ?? paxParser.adults.defaultValue) === 1} increaseDisabled={!!maxOccupancyReached} onChange={onChange} />;
    };
    $[38] = maxOccupancyReached;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== control || $[41] !== t18) {
    t19 = <PickerItem>{t17}<Controller control={control} name="adults" render={t18} /></PickerItem>;
    $[40] = control;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <CategoryPriceLabel title="Kinder" description="0 - 17 Jahre" />;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const t21 = children.length === 0;
  const t22 = !!maxOccupancyReached || !!maxChildOccupancyReached;
  let t23;
  if ($[44] !== addChild || $[45] !== children.length || $[46] !== removeChild) {
    t23 = value_1 => value_1 > children.length ? addChild() : removeChild();
    $[44] = addChild;
    $[45] = children.length;
    $[46] = removeChild;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== children.length || $[49] !== t21 || $[50] !== t22 || $[51] !== t23) {
    t24 = <PickerItem>{t20}<AmountPicker value={children.length} decreaseDisabled={t21} increaseDisabled={t22} onChange={t23} /></PickerItem>;
    $[48] = children.length;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== control || $[54] !== fields || $[55] !== formState) {
    t25 = !!fields.length && <div className="grid gap-3"><p>Alter der Kinder beim Check-In:</p>{fields.map((t26, i) => {
        const {
          id
        } = t26;
        return <Controller key={id} control={control} name={`children.${i}.value`} render={t27 => {
          const {
            field: t28
          } = t27;
          const {
            value: value_2,
            ...field
          } = t28;
          return <Select key={id} value={value_2?.toString()} {...field} options={ageOptions} data-testid={shopTestId("PaxPicker.child-age-select")} label={`Alter Kind ${i + 1} *`} error={formState.errors.children?.[i]?.value?.message} />;
        }} />;
      })}<span className="inline-block font-body-small opacity-30">*Pflichtfeld</span></div>;
    $[53] = control;
    $[54] = fields;
    $[55] = formState;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== maxOccupancyReached) {
    t26 = maxOccupancyReached && <MessageBox type="info">Die maximale Personenzahl ist erreicht.</MessageBox>;
    $[57] = maxOccupancyReached;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t19 || $[60] !== t24 || $[61] !== t25 || $[62] !== t26) {
    t27 = <div className="grid gap-8">{t19}{t24}{t25}{t26}</div>;
    $[59] = t19;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== control || $[65] !== formState || $[66] !== rooms || $[67] !== showRoomsPicker) {
    t28 = showRoomsPicker && rooms && <div className="flex flex-col gap-4" data-testid={shopTestId("PaxPicker.rooms")}><PickerItem className="items-center"><h5 className="font-heading5">Zimmer</h5><Controller control={control} name="rooms" render={_temp4} /></PickerItem>{formState.errors.rooms && <MessageBox type="error">Pro Zimmer muss mindestens ein Erwachsener angegeben werden.</MessageBox>}</div>;
    $[64] = control;
    $[65] = formState;
    $[66] = rooms;
    $[67] = showRoomsPicker;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== onDelete) {
    t29 = onDelete && <div className="flex justify-center sm:justify-start"><Button variant="text" onClick={onDelete} IconStart={TrashSimple}>Zimmer entfernen</Button></div>;
    $[69] = onDelete;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== accommodation || $[72] !== showContactInfo) {
    t30 = accommodation && showContactInfo && <PaxContactInfos accommodation={accommodation} />;
    $[71] = accommodation;
    $[72] = showContactInfo;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t27 || $[75] !== t28 || $[76] !== t29 || $[77] !== t30) {
    t31 = <div className="flex flex-col items-center py-6 *:w-full *:max-w-112 *:py-6 *:first:pt-0 *:last:pb-0">{t27}{t28}{t29}{t30}</div>;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
    $[77] = t30;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== t15 || $[80] !== t16 || $[81] !== t31) {
    t32 = <Modal.Content Root={t15} renderFooter={t16}>{t31}</Modal.Content>;
    $[79] = t15;
    $[80] = t16;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  return t32;
}

/**
 * Automatically evenly distributes adults and children across the specified number of rooms.
 */
function _temp4(t0) {
  const {
    field: t1
  } = t0;
  const {
    value: value_3,
    onChange: onChange_0
  } = t1;
  return <AmountPicker value={value_3 ?? 1} decreaseDisabled={(value_3 ?? 1) === 1} onChange={onChange_0} />;
}
function _temp3(child) {
  return child.value;
}
function _temp2(_, age) {
  return {
    label: `${age} ${age === 1 ? "Jahr" : "Jahre"}`,
    value: age.toString()
  };
}
function _temp(value) {
  return !Number.isNaN(Number(value));
}
function determineAllocation(adults: number, children: number[], rooms: number) {
  return Array.from({
    length: rooms
  }, (_, roomIndex) => {
    const adultsInThisRoom = Math.floor(adults / rooms) + (roomIndex < adults % rooms ? 1 : 0);
    const childrenPerRoom = Math.floor(children.length / rooms);
    const extraChildren = children.length % rooms;
    const startIndex = roomIndex * childrenPerRoom + Math.min(roomIndex, extraChildren);
    const childrenInThisRoom = childrenPerRoom + (roomIndex < extraChildren ? 1 : 0);
    return createAllocation({
      adults: adultsInThisRoom,
      children: children.slice(startIndex, startIndex + childrenInThisRoom)
    });
  });
}
const mapChildren = (children: number[]) => children.map(value => ({
  value
}));
type PickerItemProps = PropsWithChildren<{
  className?: string;
}>;
function PickerItem(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("flex items-start justify-between gap-3", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}